import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useOnderaannemersStore } from '@/stores/onderaannemers';
import type { AppendOutcome } from '@/stores/onderaannemers';
import type {
  OverviewInstallerGroup, OverviewDay, OverviewCollector, TierBand, TierScope,
} from '@/api/onderaannemers';
import { MEERWERK_TYPE_LABELS, type Meerwerk } from '@/api/meerwerken';
import { AppendConfirmModal } from './AppendConfirmModal';
import { MissingInstallerBlock } from './MissingInstallerBlock';
import { GeenPrijsLabel } from '@/components/ui/GeenPrijsLabel';

const DUTCH_WEEKDAYS = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
const DUTCH_MONTHS = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
];

// 'YYYY-MM-DD' → 'wo 28 mei 2026'. Local components only — no toISOString (CET shift).
function formatDayLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  const local = new Date(y, m - 1, d);
  return `${DUTCH_WEEKDAYS[local.getDay()]} ${d} ${DUTCH_MONTHS[m - 1]} ${y}`;
}

function collectorWord(n: number): string {
  return `collector${n !== 1 ? 'en' : ''}`;
}

// Dutch label for a supplier price tier (ADR-050). 'from200' is the cheaper bulk
// rate at ≥200 m²; 'under200' is the base rate.
function tierLabelNl(tier: TierBand): string {
  return tier === 'from200' ? '200+ tarief' : 'basistarief';
}

// 'Materiaallijst' for picking scope, 'Projecttotaal' for the project fallback.
function scopePrefixNl(scope: TierScope): string {
  return scope === 'materiaallijst' ? 'Materiaallijst' : 'Projecttotaal';
}

export function DailyOverviewTab() {
  const {
    overviewData,
    overviewLoading,
    overviewError,
    overviewDateFrom,
    hidePO,
    fetchDailyOverview,
    setOverviewDateFrom,
    toggleHidePO,
  } = useOnderaannemersStore(
    useShallow((s) => ({
      overviewData: s.overviewData,
      overviewLoading: s.overviewLoading,
      overviewError: s.overviewError,
      overviewDateFrom: s.overviewDateFrom,
      hidePO: s.hidePO,
      fetchDailyOverview: s.fetchDailyOverview,
      setOverviewDateFrom: s.setOverviewDateFrom,
      toggleHidePO: s.toggleHidePO,
    })),
  );

  useEffect(() => {
    fetchDailyOverview();
  }, [fetchDailyOverview]);

  // Apply the "hide PO" filter client-side: drop PO'd collectors, then keep only days
  // that still have a visible collector OR a meerwerk (meerwerken are pending, never
  // PO'd, so a meerwerk-only day survives the filter and stays on its date).
  const { groups, total } = useMemo(() => {
    if (!overviewData) return { groups: [] as OverviewInstallerGroup[], total: 0 };
    let visible = 0;
    const groups = overviewData.installerGroups
      .map((g) => {
        const days = g.days
          .map((d) => ({
            ...d,
            collectors: hidePO ? d.collectors.filter((c) => !c.hasPo) : d.collectors,
          }))
          .filter((d) => d.collectors.length > 0 || d.meerwerken.length > 0);
        const collectorCount = days.reduce((s, d) => s + d.collectors.length, 0);
        visible += collectorCount;
        return { ...g, days, collectorCount };
      })
      .filter((g) => g.days.length > 0 || g.pendingMeerwerken.length > 0);
    return { groups, total: visible };
  }, [overviewData, hidePO]);

  const hasRawData = (overviewData?.installerGroups.length ?? 0) > 0;
  const withoutInstaller = overviewData?.withoutInstaller ?? [];
  // With collectors waiting for an installer on screen, the empty-state copy must say it is
  // about collectors that CAN go on a PO, not that there is nothing at all.
  const emptyScope = withoutInstaller.length > 0 ? 'met installateur' : 'met foto’s';

  return (
    <div className="mt-5">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <label className="flex items-center gap-2 text-sm text-td-navy">
          <span className="font-medium">Vanaf</span>
          <input
            type="date"
            value={overviewDateFrom}
            onChange={(e) => setOverviewDateFrom(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue focus:border-transparent bg-white"
          />
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={hidePO}
            onChange={toggleHidePO}
            className="w-4 h-4 rounded border-gray-300 text-td-blue focus:ring-td-blue/40"
          />
          <span className="text-sm font-medium text-gray-600">Verberg met PO</span>
        </label>
      </div>

      {overviewLoading && !overviewData ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
            <span className="sr-only">Laden...</span>
          </div>
          <p className="text-td-muted text-sm mt-3">Dagoverzicht laden...</p>
        </div>
      ) : overviewError ? (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="text-sm font-semibold whitespace-pre-line">{overviewError}</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-td-muted font-medium">
            {groups.length > 0
              ? `${total} ${collectorWord(total)} over ${groups.length} onderaannemer${groups.length !== 1 ? 's' : ''}`
              : withoutInstaller.length > 0
                ? 'Geen collectoren met installateur om op een PO te zetten'
                : 'Niets te tonen voor deze selectie'}
          </p>

          <MissingInstallerBlock collectors={withoutInstaller} />

          {groups.length === 0 && (
            <div className="text-center py-12">
              <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-400 text-xs mt-1">
                {hasRawData && hidePO
                  ? `Alle collectoren ${emptyScope} vanaf deze datum hebben al een inkooporder.`
                  : `Geen collectoren ${emptyScope} vanaf deze datum.`}
              </p>
            </div>
          )}

          <div className="mt-4 space-y-3">
            {groups.map((group) => (
              <InstallerGroupCard key={group.installerId} group={group} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function InstallerGroupCard({ group }: { group: OverviewInstallerGroup }) {
  const [expanded, setExpanded] = useState(true);
  const [result, setResult] = useState<AppendOutcome | null>(null);
  // Tier confirm step (ADR-050): the add button opens this modal first; the actual
  // append only runs after "Bevestig toevoegen".
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Tier overrides chosen in the confirm popup, kept so the "Ga door zonder deze N"
  // allowSkip retry re-applies the same manual tiers instead of falling back to auto.
  const [pendingTierOverrides, setPendingTierOverrides] = useState<Record<number, TierBand>>({});
  const [pendingCustomRates, setPendingCustomRates] = useState<Record<string, number>>({});

  const {
    selectedIds, geplaatstIds, meerwerkIds, appending,
    toggleCollectorSelection, toggleGeplaatst, toggleMeerwerkSelection, appendSelected,
  } = useOnderaannemersStore(
    useShallow((s) => ({
      // Select the raw (stable) array reference; default to undefined here and
      // normalise below so the selector never returns a fresh `[]` each render.
      selectedIds: s.selection[group.installerId],
      geplaatstIds: s.geplaatstSelection[group.installerId],
      meerwerkIds: s.meerwerkSelection[group.installerId],
      appending: s.appendingInstallerId === group.installerId,
      toggleCollectorSelection: s.toggleCollectorSelection,
      toggleGeplaatst: s.toggleGeplaatst,
      toggleMeerwerkSelection: s.toggleMeerwerkSelection,
      appendSelected: s.appendSelected,
    })),
  );

  const selectedSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);
  const geplaatstSet = useMemo(() => new Set(geplaatstIds ?? []), [geplaatstIds]);
  const meerwerkSet = useMemo(() => new Set(meerwerkIds ?? []), [meerwerkIds]);
  const collectorCount = selectedIds?.length ?? 0;
  const meerwerkCount = meerwerkIds?.length ?? 0;
  const selectedCount = collectorCount + meerwerkCount;
  const geplaatstCount = geplaatstIds?.length ?? 0;

  // The OverviewCollector objects currently selected, for the confirm modal's
  // tier grouping. Flattened from this group's days.
  const selectedCollectors = useMemo(
    () => group.days.flatMap((d) => d.collectors).filter((c) => selectedSet.has(c.id)),
    [group.days, selectedSet],
  );
  // The selected meerwerken, for the confirm modal's read-only summary.
  const selectedMeerwerken = useMemo(
    () => group.pendingMeerwerken.filter((m) => meerwerkSet.has(m.id)),
    [group.pendingMeerwerken, meerwerkSet],
  );

  const submit = async (
    allowSkip: boolean,
    tierOverrides?: Record<number, TierBand>,
    customSystemRates?: Record<string, number>,
    acknowledgedGeleverd?: boolean,
  ) => {
    setConfirmOpen(false);
    // First submit (from the popup) carries the overrides + custom rates; the allowSkip retry
    // reuses the remembered sets so manual tiers and €/m² rates survive dropping already-on-PO
    // collectors. Meerwerk selection lives in the store, so it survives the retry as-is.
    const effectiveOverrides = tierOverrides ?? pendingTierOverrides;
    if (tierOverrides) setPendingTierOverrides(tierOverrides);
    const effectiveRates = customSystemRates ?? pendingCustomRates;
    if (customSystemRates) setPendingCustomRates(customSystemRates);

    // appendSelected reads collector + meerwerk selection from the store and awaits
    // fetchDailyOverview(), which returns fresh groups — on_po meerwerken drop out automatically.
    const outcome = await appendSelected(group.installerId, {
      allowSkip,
      ...(acknowledgedGeleverd ? { acknowledgedGeleverd: true } : {}),
      ...(Object.keys(effectiveOverrides).length > 0 ? { tierOverrides: effectiveOverrides } : {}),
      ...(Object.keys(effectiveRates).length > 0 ? { customSystemRates: effectiveRates } : {}),
    });

    // 409 COLLECTOR_LIKELY_PREPLACED: the fase is flagged "Collectoren geleverd" and
    // the request didn't acknowledge it. Show window.confirm, then retry with the flag.
    if (outcome.status === 'geleverd_confirm') {
      const lines = outcome.flagged
        .map((f) => `- ${f.name}${f.faseName ? ` (${f.faseName})` : ''}`)
        .join('\n');
      const confirmed = window.confirm(
        `De volgende collectoren zijn mogelijk al door de klant geplaatst:\n${lines}\n\nToch volledig billen en op PO zetten?`,
      );
      if (confirmed) {
        // Tier overrides and custom rates are now in pendingTierOverrides / pendingCustomRates
        // (updated above), so the recursive call picks them up via the ?? fallback.
        await submit(allowSkip, undefined, undefined, true);
      }
      // Cancelled: leave result as-is (no banner, user explicitly aborted).
      return;
    }

    setResult(outcome);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
          aria-expanded={expanded}
        >
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`}
            fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <span className="flex-1 min-w-0">
            <span className="text-sm font-bold text-td-navy block truncate">{group.installerName}</span>
            <span className="text-xs text-gray-400">{group.vendorName}</span>
          </span>
          <span className="flex items-center gap-1.5 flex-shrink-0">
            {group.collectorCount > 0 && (
              <span className="text-xs font-semibold bg-td-blue/10 text-td-blue px-2 py-0.5 rounded-full">
                {group.collectorCount} {collectorWord(group.collectorCount)}
              </span>
            )}
            {group.pendingMeerwerken.length > 0 && (
              <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                {group.pendingMeerwerken.length} meerwerk{group.pendingMeerwerken.length !== 1 ? 'en' : ''}
              </span>
            )}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={selectedCount === 0 || appending}
          className="flex-shrink-0 px-3 py-1.5 text-xs font-bold text-white bg-td-blue rounded-lg hover:bg-td-blue/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {appending
            ? 'Bezig…'
            : selectedCount > 0
              ? `Voeg toe aan PO (${selectedCount})`
              : 'Voeg toe aan PO'}
        </button>
      </div>

      {confirmOpen && (
        <AppendConfirmModal
          installerName={group.installerName}
          selectedCollectors={selectedCollectors}
          geplaatstCount={geplaatstCount}
          vendorSingleRate={group.vendorSingleRate}
          appending={appending}
          selectedMeerwerken={selectedMeerwerken}
          onConfirm={(tierOverrides, customSystemRates) =>
            submit(false, tierOverrides, customSystemRates)
          }
          onCancel={() => setConfirmOpen(false)}
        />
      )}

      {/* Append result — success / 207 warnings / 409 conflicts / 400 error */}
      {result && (
        <AppendResultBanner
          result={result}
          appending={appending}
          onRetrySkip={() => submit(true)}
          onRefresh={() => useOnderaannemersStore.getState().fetchDailyOverview()}
          onDismiss={() => setResult(null)}
        />
      )}

      {expanded && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {group.days.map((day) => (
            <DaySection
              key={day.date}
              day={day}
              installerId={group.installerId}
              vendorSingleRate={group.vendorSingleRate}
              selectedSet={selectedSet}
              geplaatstSet={geplaatstSet}
              meerwerkSet={meerwerkSet}
              onToggle={toggleCollectorSelection}
              onToggleGeplaatst={toggleGeplaatst}
              onToggleMeerwerk={toggleMeerwerkSelection}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface MeerwerkRowProps {
  meerwerk: Meerwerk;
  installerId: number;
  selected: boolean;
  onToggle: (installerId: number, meerwerkId: number) => void;
}

/** A single pending meerwerk row — selectable (checkbox) exactly like a collector row,
 *  feeding the same "Voeg toe aan PO" append. Rendered inside DaySection on its own date,
 *  amber-tinted + badged so it stays distinguishable from the collector rows around it. */
function MeerwerkRow({ meerwerk: m, installerId, selected, onToggle }: MeerwerkRowProps) {
  const isKm = m.type === 'kilometervergoeding' || m.unit === 'km';
  // null = no price (a km line the server would refuse): no amount, no total.
  const price = m.effective_unit_price;
  const total = price === null ? null : m.quantity * price;
  return (
    <label
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors border-l-2 ${
        selected ? 'bg-td-blue/10 border-td-blue/40' : 'bg-amber-50/60 border-amber-300 hover:bg-amber-50'
      }`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggle(installerId, m.id)}
        className="w-4 h-4 rounded border-gray-300 text-td-blue focus:ring-td-blue/40 flex-shrink-0"
        aria-label={`Selecteer meerwerk ${m.name}${m.project_name ? ` van ${m.project_name}` : ''}`}
      />
      <span className="flex-1 min-w-0">
        {m.project_name && (
          <span className="text-xs font-bold text-td-blue block truncate">{m.project_name}</span>
        )}
        <span className="text-sm text-td-navy">{m.name}</span>
        <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
          Meerwerk · {MEERWERK_TYPE_LABELS[m.type] ?? m.type}
        </span>
      </span>
      <span className="text-right text-xs text-td-muted flex-shrink-0 whitespace-nowrap">
        {price === null ? (
          <>
            {isKm ? `${m.quantity} km` : `${m.quantity}`}
            <GeenPrijsLabel reason={m.price_unavailable_reason} className="block max-w-[16rem]" />
          </>
        ) : (
          isKm ? `${m.quantity} km × €${price.toFixed(2)}` : `${m.quantity} × €${price.toFixed(2)}`
        )}
        {total !== null && total > 0 && <span className="block font-semibold text-td-navy">€{total.toFixed(2)}</span>}
      </span>
    </label>
  );
}

interface DaySectionProps {
  day: OverviewDay;
  installerId: number;
  vendorSingleRate: boolean;
  selectedSet: Set<number>;
  geplaatstSet: Set<number>;
  meerwerkSet: Set<number>;
  onToggle: (installerId: number, collectorId: number) => void;
  onToggleGeplaatst: (installerId: number, collectorId: number) => void;
  onToggleMeerwerk: (installerId: number, meerwerkId: number) => void;
}

function DaySection({
  day,
  installerId,
  vendorSingleRate,
  selectedSet,
  geplaatstSet,
  meerwerkSet,
  onToggle,
  onToggleGeplaatst,
  onToggleMeerwerk,
}: DaySectionProps) {
  const meerwerken = day.meerwerken ?? [];
  // "3 collectoren · 1 meerwerk" — either part omitted when zero (a day can now be
  // meerwerk-only). Collectors first so the count reads left-to-right with the rows.
  const countLabel = [
    day.collectors.length > 0 ? `${day.collectors.length} ${collectorWord(day.collectors.length)}` : null,
    meerwerken.length > 0 ? `${meerwerken.length} meerwerk${meerwerken.length !== 1 ? 'en' : ''}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold text-td-navy uppercase tracking-wide">
          {formatDayLabel(day.date)}
        </h4>
        <span className="text-xs text-gray-400">{countLabel}</span>
      </div>
      <div className="space-y-1.5">
        {day.collectors.map((c) => (
          <CollectorRow
            key={c.id}
            collector={c}
            installerId={installerId}
            vendorSingleRate={vendorSingleRate}
            selected={selectedSet.has(c.id)}
            geplaatst={geplaatstSet.has(c.id)}
            onToggle={onToggle}
            onToggleGeplaatst={onToggleGeplaatst}
          />
        ))}
        {meerwerken.map((m) => (
          <MeerwerkRow
            key={`mw-${m.id}`}
            meerwerk={m}
            installerId={installerId}
            selected={meerwerkSet.has(m.id)}
            onToggle={onToggleMeerwerk}
          />
        ))}
      </div>
    </div>
  );
}

interface CollectorRowProps {
  collector: OverviewCollector;
  installerId: number;
  vendorSingleRate: boolean;
  selected: boolean;
  geplaatst: boolean;
  onToggle: (installerId: number, collectorId: number) => void;
  onToggleGeplaatst: (installerId: number, collectorId: number) => void;
}

function CollectorRow({
  collector,
  installerId,
  vendorSingleRate,
  selected,
  geplaatst,
  onToggle,
  onToggleGeplaatst,
}: CollectorRowProps) {
  const navigate = useNavigate();
  const meta = [
    collector.blok || null,
    collector.verdieping || null,
    collector.systeem || null,
    collector.m2 ? `${Math.round(collector.m2)} m²` : null,
  ].filter(Boolean);

  return (
    <div
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors ${
        selected ? 'bg-td-blue/10' : 'bg-gray-50 hover:bg-td-blue/5'
      }`}
    >
      {/* Checkbox — separate hit target; ticking must NOT navigate */}
      <label
        className="flex items-center justify-center p-1 cursor-pointer flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(installerId, collector.id)}
          className="w-4 h-4 rounded border-gray-300 text-td-blue focus:ring-td-blue/40"
          aria-label={`Selecteer collector ${collector.name} van ${collector.projectName}`}
        />
      </label>

      {/* Body — navigates to the project's inkooporders tab */}
      <button
        type="button"
        onClick={() => navigate(`/project/${collector.projectId}?tab=inkooporders`)}
        className="flex-1 min-w-0 flex items-center gap-3 text-left group"
      >
        <span className="flex-1 min-w-0">
          <span className="text-sm font-medium text-td-navy group-hover:text-td-blue transition-colors block truncate">
            {collector.projectName}
          </span>
          <span className="text-xs text-gray-400 truncate block">
            {collector.name}
            {meta.length > 0 && <span className="text-gray-300"> · {meta.join(' · ')}</span>}
          </span>
        </span>
        {/* Tier preview (ADR-050): basis m² that decides this collector's supplier tier.
            Single-rate vendor (TD-080) → one flat rate, so the band is meaningless: "vast tarief". */}
        {vendorSingleRate ? (
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 bg-td-blue/10 text-td-blue"
            title={`${scopePrefixNl(collector.tierScope)} ${collector.tierLabel}: één vast tarief (geen volumekorting)`}
          >
            vast tarief
          </span>
        ) : (
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
              collector.tier === 'from200'
                ? 'bg-td-green/15 text-td-green'
                : 'bg-gray-100 text-gray-500'
            }`}
            title={`${scopePrefixNl(collector.tierScope)} ${collector.tierLabel}: ${Math.round(collector.basisM2)} m² → ${tierLabelNl(collector.tier)}`}
          >
            {Math.round(collector.basisM2)} m² · {tierLabelNl(collector.tier)}
          </span>
        )}
        {collector.hasPo ? (
          <span className="inline-block max-w-[10rem] truncate text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex-shrink-0" title={collector.poName ?? undefined}>
            {collector.poName ?? 'PO'}
          </span>
        ) : (
          <span className="text-xs font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full flex-shrink-0">
            Geen PO
          </span>
        )}
        {collector.fotosStatus === 'Fotos_Goedgekeurd' ? (
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 bg-[#29c4a9]/15 text-[#29c4a9]"
            data-testid="fotos-status-pill"
          >
            Goedgekeurd
          </span>
        ) : collector.fotosStatus === 'Fotos_Geupload' ? (
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 bg-amber-100 text-amber-700"
            data-testid="fotos-status-pill"
          >
            Geüpload
          </span>
        ) : null}
      </button>

      {/* "Collectoren geleverd" — replaces the old (unreliable, link-presence) "Op materiaallijst"
          marker. Authoritative flag ⇒ amber pill warning the client placed these collectors (don't
          bill placement). Soft signals ⇒ low-key note. Otherwise nothing. Puur informatief. */}
      {collector.geleverd?.flagged ? (
        <span
          className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 bg-amber-500 text-white"
          title={`Deze fase is gemarkeerd als "Collectoren geleverd"${collector.geleverd.faseName ? ` (${collector.geleverd.faseName})` : ''} — mogelijk al door de klant geplaatst.${collector.geleverd.faseNote ? `\n${collector.geleverd.faseNote}` : ''}`}
        >
          Collectoren geleverd
        </span>
      ) : (collector.geleverd?.source === 'project_flagged' || collector.geleverd?.hint) ? (
        <span
          className="text-xs italic whitespace-nowrap flex-shrink-0 text-gray-400"
          title={`Mogelijk al geplaatst — controleer${collector.geleverd.faseName ? ` (${collector.geleverd.faseName})` : ''}${collector.geleverd.faseNote ? `\n${collector.geleverd.faseNote}` : ''}`}
        >
          Mogelijk geplaatst?
        </span>
      ) : null}

      {/* "Al geplaatst" — only once selected and without a PO. Marking it adds a negative
          minprijs correction line on the PO. Separate hit target; must NOT navigate. */}
      {selected && !collector.hasPo && (
        <label
          className="flex items-center gap-1 text-xs font-medium text-amber-700 whitespace-nowrap flex-shrink-0 cursor-pointer pr-1"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={geplaatst}
            onChange={() => onToggleGeplaatst(installerId, collector.id)}
            className="w-3.5 h-3.5 rounded border-amber-300 text-amber-600 focus:ring-amber-400/40"
            aria-label={`Markeer collector ${collector.name} als al geplaatst`}
          />
          Al geplaatst
        </label>
      )}
    </div>
  );
}

interface AppendResultBannerProps {
  result: AppendOutcome;
  appending: boolean;
  onRetrySkip: () => void;
  onRefresh: () => void;
  onDismiss: () => void;
}

function AppendResultBanner({ result, appending, onRetrySkip, onRefresh, onDismiss }: AppendResultBannerProps) {
  // geleverd_confirm is handled inline in submit() before setResult — it never reaches here.
  if (result.status === 'geleverd_confirm') return null;

  if (result.status === 'ok') {
    const { result: r } = result;
    const hasWarnings = (r.warnings?.length ?? 0) > 0 || (r.partialFailures?.length ?? 0) > 0;
    const tone = hasWarnings
      ? 'bg-amber-50 border-amber-200 text-amber-800'
      : 'bg-green-50 border-green-200 text-green-800';
    const verb = r.created ? 'nieuwe PO aangemaakt' : 'toegevoegd';
    // Tier transparency (ADR-050): compact per-scope summary from the response. A single-rate
    // vendor (TD-080) has one flat rate, so we show "vast tarief" instead of the m²→band.
    const tierSummary = (r.tierInfo ?? [])
      .map((t) => t.singleRate
        ? `${scopePrefixNl(t.scope)} ${t.label} → vast tarief`
        : `${scopePrefixNl(t.scope)} ${t.label} ${Math.round(t.totalM2)} m² → ${tierLabelNl(t.tier)}`)
      .join(' · ');
    return (
      <div className={`border-t ${tone} px-4 py-3 text-sm`} role="status">
        <div className="flex items-start justify-between gap-3">
          <p className="font-semibold">
            Toegevoegd aan {r.poName} — {r.appended} {collectorWord(r.appended)} ({verb})
            {r.skipped.length > 0 && (
              <span className="font-normal"> · {r.skipped.length} overgeslagen (stonden al op een PO)</span>
            )}
            {tierSummary && (
              <span className="font-normal"> · {tierSummary}</span>
            )}
          </p>
          <DismissButton onDismiss={onDismiss} />
        </div>

        {(r.warnings?.length ?? 0) > 0 && (
          <ul className="mt-2 list-disc list-inside space-y-0.5 text-xs">
            {r.warnings!.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        )}

        {(r.partialFailures?.length ?? 0) > 0 && (
          <div className="mt-2 text-xs">
            <p className="font-semibold">Niet volledig verwerkt — handmatige actie nodig:</p>
            <ul className="mt-1 space-y-1">
              {r.partialFailures!.map((pf, i) => (
                <li key={i} className="rounded-md bg-white/60 border border-amber-200 px-2 py-1.5">
                  <span className="font-semibold">{pf.name}</span>
                  <span className="block text-amber-700">Oplossing: {pf.fix}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  if (result.status === 'conflict') {
    const isConcurrent = result.code === 'CONCURRENT_APPEND';
    // Partial COLLECTOR_ALREADY_ON_PO (some on a PO) carries poName + no message →
    // offer "Ga door zonder deze N". The all-conflict variant carries a message.
    const canSkip = result.code === 'COLLECTOR_ALREADY_ON_PO' && !result.message;
    const n = result.conflicts.length;
    return (
      <div className="border-t bg-amber-50 border-amber-200 text-amber-800 px-4 py-3 text-sm" role="alert">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-semibold">
              {isConcurrent
                ? 'Tegelijk toegevoegd door een andere aanvraag'
                : result.message ?? `${n} ${collectorWord(n)} ${n !== 1 ? 'staan' : 'staat'} al op een PO`}
            </p>
            {result.conflicts.length > 0 && (
              <ul className="mt-1 text-xs space-y-0.5">
                {result.conflicts.map((c) => (
                  <li key={c.collectorId}>
                    Collector #{c.collectorId}
                    {c.poName ? ` — staat op ${c.poName}` : ''}
                  </li>
                ))}
              </ul>
            )}
            {isConcurrent && (
              <p className="mt-1 text-xs">Ververs het overzicht en probeer opnieuw.</p>
            )}
          </div>
          <DismissButton onDismiss={onDismiss} />
        </div>

        <div className="mt-2 flex items-center gap-3">
          {canSkip && (
            <button
              type="button"
              onClick={onRetrySkip}
              disabled={appending}
              className="px-3 py-1.5 text-xs font-bold text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
            >
              {appending ? 'Bezig…' : `Ga door zonder deze ${n}`}
            </button>
          )}
          {isConcurrent && (
            <button
              type="button"
              onClick={onRefresh}
              className="px-3 py-1.5 text-xs font-bold text-amber-800 border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors"
            >
              Ververs overzicht
            </button>
          )}
        </div>
      </div>
    );
  }

  // status === 'error' (400s + unexpected)
  return (
    <div className="border-t bg-red-50 border-red-200 text-red-700 px-4 py-3 text-sm" role="alert">
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold whitespace-pre-line">{result.message}</p>
        <DismissButton onDismiss={onDismiss} />
      </div>
    </div>
  );
}

function DismissButton({ onDismiss }: { onDismiss: () => void }) {
  return (
    <button
      type="button"
      onClick={onDismiss}
      className="flex-shrink-0 text-xs font-semibold opacity-70 hover:opacity-100"
      aria-label="Sluiten"
    >
      ✕
    </button>
  );
}
